// app/components/common/Sidebar.jsx
import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    Home, Settings, Users, LogOut, User,
    Shield, Code, AlertCircle, ChevronDown, ChevronRight, Search, Bell, FileText, ChevronLeft, ClipboardList,
    Building2, DollarSign, Layers, ListChecks, Briefcase, UserCog, GitBranch, MessageSquare, Gauge, CreditCard, MapPin, BarChart3,
    Scale
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { useBrandedImages, useBranding } from '../../hooks/useBranding';

// Walk the menu tree and return the ancestor group ids for the leaf whose
// path matches the current location, so that group auto-expands on navigation.
const findActiveGroupIds = (items, pathname, parents = []) => {
    for (const item of items) {
        if (item.children && item.children.length) {
            const found = findActiveGroupIds(item.children, pathname, [...parents, item.id]);
            if (found) return found;
        } else if (item.path && (pathname === item.path || pathname.startsWith(item.path + '/'))) {
            return parents;
        }
    }
    return null;
};

const Sidebar = ({ isOpen, setIsOpen }) => {
    const { user, logout } = useAuth();
    const { hasRouteAccess } = useRoleAccess();
    const { company } = useBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedMenus, setExpandedMenus] = React.useState(['operations']);
    
    // Get branded content
    const { getSidebarLogo } = useBrandedImages();    

    const toggleMenu = (menuId) => {
        setExpandedMenus(prev =>
            prev.includes(menuId)
                ? prev.filter(id => id !== menuId)
                : [...prev, menuId]
        );
    };

    const menuItems = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: Home,
            path: '/dashboard',
            requiredPermission: 'dashboard'
        },
        {
            id: 'operations',
            label: 'Operations',
            icon: Briefcase,
            children: [
                {
                    id: 'case-booking',
                    label: 'Case Booking',
                    icon: Search,
                    path: '/case-booking',
                    requiredPermission: 'case-booking'
                },
                {
                    id: 'services',
                    label: 'Services',
                    icon: Bell,
                    path: '/services',
                    requiredPermission: 'services'
                },
                {
                    id: 'dispatch-queue',
                    label: 'Dispatch Queue',
                    icon: AlertCircle,
                    path: '/dispatch-queue',
                    requiredPermission: 'services'
                },
                {
                    id: 'disputes',
                    label: 'Disputes',
                    icon: Scale,
                    path: '/disputes',
                    requiredPermission: 'disputes'
                },
                {
                    id: 'case-history',
                    label: 'Case History',
                    icon: FileText,
                    path: '/case-history',
                    requiredPermission: 'case-history'
                },
                {
                    id: 'invoices',
                    label: 'Invoices',
                    icon: FileText,
                    path: '/invoices',
                    requiredPermission: 'invoices'
                },
            ]
        },
        {
            id: 'vendors',
            label: 'Vendors',
            icon: Building2,
            children: [
                {
                    id: 'vendor-locations',
                    label: 'Vendor Locations',
                    icon: Building2,
                    path: '/vendor-locations',
                    requiredPermission: 'vendor-locations'
                },
                {
                    id: 'vendor-performance',
                    label: 'Vendor Performance',
                    icon: Gauge,
                    path: '/vendor-performance',
                    requiredPermission: 'vendor-locations'
                },
                {
                    id: 'vendor-subscriptions',
                    label: 'Subscriptions',
                    icon: CreditCard,
                    path: '/vendor-subscriptions',
                    requiredPermission: 'vendor-locations'
                },
                {
                    id: 'referrals',
                    label: 'Referrals',
                    icon: Users,
                    path: '/referrals',
                    requiredPermission: 'referrals'
                },
                // Only show vendor registrations for SFA branding
                ...(company.name.toLowerCase() === 'sfa' ? [{
                    id: 'vendor-registrations',
                    label: 'Vendor Registrations',
                    icon: ClipboardList,
                    path: '/vendor-registrations',
                    requiredPermission: 'vendor-registrations'
                }] : []),
            ]
        },
        {
            id: 'reports',
            label: 'Reports',
            icon: BarChart3,
            children: [
                {
                    id: 'revenue-report',
                    label: 'Revenue',
                    icon: BarChart3,
                    path: '/reports/revenue',
                    requiredPermission: 'reports'
                },
                {
                    id: 'vendor-sla-report',
                    label: 'Vendor SLA',
                    icon: Gauge,
                    path: '/reports/vendor-sla',
                    requiredPermission: 'reports'
                },
            ]
        },
        {
            id: 'maintenance',
            label: 'Maintenance',
            icon: Settings,
            children: [
                {
                    id: 'roles',
                    label: 'Roles & Permissions',
                    icon: Shield,
                    path: '/maintenance/roles',
                    requiredPermission: 'roles'
                },
                {
                    id: 'codes',
                    label: 'Common Codes',
                    icon: Code,
                    path: '/maintenance/common-codes',
                    requiredPermission: 'common-codes'
                },
                {
                    id: 'causes',
                    label: 'Cause of Calls',
                    icon: AlertCircle,
                    path: '/maintenance/cause-of-calls',
                    requiredPermission: 'cause-of-calls'
                },
                {
                    id: 'diagnostics',
                    label: 'Diagnostic Flows',
                    icon: GitBranch,
                    path: '/maintenance/diagnostics',
                    requiredPermission: 'cause-of-calls'
                },
                {
                    id: 'dealers',
                    label: 'Dealers',
                    icon: Building2,
                    path: '/maintenance/dealers',
                    requiredPermission: 'dealers'
                },
                {
                    id: 'client-service-rates',
                    label: 'Service Rates',
                    icon: DollarSign,
                    path: '/maintenance/client-service-rates',
                    requiredPermission: 'client-service-rates'
                },
                {
                    id: 'regions',
                    label: 'Regions & Fees',
                    icon: MapPin,
                    path: '/maintenance/regions',
                    requiredPermission: 'client-service-rates'
                },
                {
                    id: 'scheme-service-setup',
                    label: 'Service Types & Subtypes',
                    icon: Layers,
                    path: '/maintenance/scheme-service-setup',
                    requiredPermission: 'scheme-service-setup'
                },
                {
                    id: 'service-statuses',
                    label: 'Service Statuses',
                    icon: ListChecks,
                    path: '/maintenance/service-statuses',
                    requiredPermission: 'service-statuses'
                },
                {
                    id: 'sms-templates',
                    label: 'SMS Templates',
                    icon: MessageSquare,
                    path: '/maintenance/sms-templates',
                    requiredPermission: 'sms-templates'
                },
            ]
        },
        {
            id: 'administration',
            label: 'Administration',
            icon: UserCog,
            children: [
                {
                    id: 'users',
                    label: 'Users',
                    icon: Users,
                    path: '/users',
                    requiredPermission: 'users'
                },
                {
                    id: 'configuration',
                    label: 'Configuration',
                    icon: Settings,
                    path: '/configuration',
                    requiredPermission: 'configuration'
                },
            ]
        },
    ];

    // Auto-expand the group that contains the currently active route.
    React.useEffect(() => {
        const active = findActiveGroupIds(menuItems, location.pathname) || [];
        if (active.length) {
            setExpandedMenus(prev => Array.from(new Set([...prev, ...active])));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    // Filter the menu tree by permissions, recursively.
    // A group (item with children) is shown only if at least one descendant is
    // visible; a leaf is shown if the user has access to its required permission.
    const filterMenuItems = (items) => {
        return items.reduce((acc, item) => {
            if (item.children && item.children.length > 0) {
                const visibleChildren = filterMenuItems(item.children);
                if (visibleChildren.length > 0) {
                    acc.push({ ...item, children: visibleChildren });
                }
            } else if (item.requiredPermission) {
                if (hasRouteAccess(item.requiredPermission)) {
                    acc.push(item);
                }
            } else {
                acc.push(item);
            }
            return acc;
        }, []);
    };

    const renderMenuItem = (item, isChild = false) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedMenus.includes(item.id);
        const Icon = item.icon;

        if (hasChildren) {
            return (
                <li key={item.id}>
                    <button
                        onClick={() => toggleMenu(item.id)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-colors text-white hover:bg-gray-800 hover:text-white ${isChild ? 'ml-4' : ''
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            {isOpen && <span>{item.label}</span>}
                        </div>
                        {isOpen && (
                            isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                        )}
                    </button>
                    {isOpen && isExpanded && (
                        <ul className="mt-1 space-y-1 ml-4 pl-2 border-l border-gray-700/60">
                            {item.children.map(child => renderMenuItem(child, true))}
                        </ul>
                    )}
                </li>
            );
        }

        const ItemIcon = item.icon;
        return (
            <li key={item.id}>
                <NavLink
                    to={item.path}
                    className={({ isActive }) => `
                        w-full flex items-center gap-4 px-5 py-2 rounded-lg transition-colors
                        ${isActive
                            ? 'bg-gray-600 text-white rounded-sm'
                            : 'text-white hover:bg-gray-600 rounded-sm hover:text-white'
                        }
                        ${isChild ? 'ml-0' : ''}
                    `}
                >
                    <ItemIcon className="w-5 h-5 flex-shrink-0" />
                    {isOpen && <span>{item.label}</span>}
                </NavLink>
            </li>
        );
    };

    // Get filtered menu items based on user permissions
    const visibleMenuItems = filterMenuItems(menuItems);

    return (
        <div className={`${isOpen ? 'w-64' : 'w-20'} sidebarbg transition-all duration-300 flex flex-col`}>          

            <nav className="flex-1 px-4 pb-4 plr-0 navbgMain">

            <div className="p-3">
                <div className="items-center justify-between logoheader relative">
                    <div className={`flex items-center logoheight gap-3 ${!isOpen && 'justify-center'}`}>
                        <div className="flex items-center justify-center h-12">
                            <img 
                                src={getSidebarLogo(isOpen)} 
                                alt="Company Logo" 
                                className={`w-auto object-contain transition-all duration-300 ${
                                    isOpen ? 'h-auto' : 'h-auto'
                                }`}
                            />
                        </div>
                        

<button
                        onClick={() => setIsOpen(!isOpen)}
                        className="text-gray-400 hover:text-white transition-colors colose-btn"
                    >
                        {isOpen ? <ChevronLeft className="w-7 h-7 close-right" /> : <ChevronRight className="w-7 h-7 open-right" />}
                    </button>
                    </div>
                   
                </div>
            </div>

                <ul className="space-y-2  p-3">
                    {visibleMenuItems.map(item => renderMenuItem(item))}
                </ul>
                <div className="p-4">
                <div className={`flex items-center gap-3 mb-3 ${!isOpen && 'justify-center'}`}>
                    <div className="w-8 h-8 bg-scarlet-600 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                    </div>
                    {isOpen && (
                        <div className="flex-1">
                            <p className="text-scarlet text-sm font-medium">{user?.name || 'Admin'}</p>
                            <p className="text-gray-400 text-xs capitalize">{user?.role || 'Administrator'}</p>
                        </div>
                    )}
                </div>
                <button
                    onClick={handleLogout}
                    className={`mt-4 w-full flex items-center rounded-sm gap-3 px-3 py-2 text-white hover:text-white transition-colors ${!isOpen && 'justify-center'
                        }`}
                >
                    <LogOut className="w-5 h-5" />
                    {isOpen && <span>Logout</span>}
                </button>
            </div>
            </nav>

            
        </div>
    );
};

export default Sidebar; 